import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Target, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const slug = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `reto_${Date.now()}`;
const modeOf = (req) => (req === 'all' || req === 'half_plus_one' ? req : 'count');

export default function ChallengeEditor({ eventId, waypoints, challenges, onChange }) {
  const [loadingPreset, setLoadingPreset] = useState(false);
  const categories = [...new Set((waypoints || []).map((w) => (w.category || '').toUpperCase()).filter(Boolean))].sort();
  const list = challenges || [];

  const update = (i, patch) => onChange(list.map((c, k) => (k === i ? { ...c, ...patch } : c)));
  const updateRule = (i, j, patch) => update(i, { rules: list[i].rules.map((r, k) => (k === j ? { ...r, ...patch } : r)) });

  const loadPreset = async () => {
    setLoadingPreset(true);
    try {
      const r = await axios.post(`${API}/admin/events/${eventId}/challenges/vestigia-preset`);
      onChange(r.data.challenges);
      toast.success('Retos Nómada y Corpus cargados y guardados');
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudieron cargar los retos'); } finally { setLoadingPreset(false); }
  };

  return (
    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 space-y-3" data-testid="challenge-editor">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1"><Target className="w-4 h-4 text-orange-500" /> Retos del evento</p>
          <p className="text-xs text-gray-600">Cada reto suma reglas por categoría: todos, la mitad más uno, o un número fijo. Sin retos, se generan automáticamente "Todos" y uno por categoría.</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={loadPreset} disabled={loadingPreset || !categories.length} className="border-violet-300 text-violet-800 h-9" data-testid="challenge-preset-btn">
          {loadingPreset ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />} Cargar retos VESTIGIA (Nómada + Corpus)
        </Button>
      </div>
      {!categories.length && <p className="text-xs text-amber-700">Importa primero los waypoints con categoría para poder definir reglas.</p>}

      {list.map((ch, i) => (
        <div key={ch.id || i} className="rounded-md bg-white border border-gray-200 p-3 space-y-2" data-testid={`challenge-${ch.id || i}`}>
          <div className="flex gap-2 items-center">
            <Input value={ch.name || ''} placeholder="Nombre del reto (ej. Nómada)" className="h-9"
              onChange={(e) => update(i, { name: e.target.value, id: slug(e.target.value) })}
              data-testid={`challenge-name-${i}`} />
            <Button type="button" size="icon" variant="ghost" className="text-red-600 shrink-0" onClick={() => onChange(list.filter((_, k) => k !== i))} data-testid={`challenge-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5">
            {(ch.rules || []).map((r, j) => (
              <div key={j} className="grid grid-cols-[1fr_1fr_auto_auto] gap-1.5 items-center" data-testid={`challenge-rule-${i}-${j}`}>
                <select value={r.category} onChange={(e) => updateRule(i, j, { category: e.target.value })} className="h-9 rounded-md border border-gray-300 px-2 text-sm bg-white">
                  <option value="*">Todas las categorías</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={modeOf(r.required)} onChange={(e) => updateRule(i, j, { required: e.target.value === 'count' ? 1 : e.target.value })} className="h-9 rounded-md border border-gray-300 px-2 text-sm bg-white">
                  <option value="all">Todos</option>
                  <option value="half_plus_one">La mitad más uno</option>
                  <option value="count">Un número</option>
                </select>
                {modeOf(r.required) === 'count'
                  ? <Input type="number" min="1" value={r.required} onChange={(e) => updateRule(i, j, { required: parseInt(e.target.value) || 1 })} className="h-9 w-20" />
                  : <span className="w-20" />}
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-gray-500" onClick={() => update(i, { rules: ch.rules.filter((_, k) => k !== j) })}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => update(i, { rules: [...(ch.rules || []), { category: categories[0] || '*', required: 'all' }] })} data-testid={`challenge-add-rule-${i}`}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Añadir regla
            </Button>
          </div>
        </div>
      ))}

      <Button type="button" size="sm" variant="outline" className="h-9" onClick={() => onChange([...list, { id: `new_${Date.now()}`, name: '', rules: [{ category: '*', required: 'all' }] }])} data-testid="challenge-add-btn">
        <Plus className="w-4 h-4 mr-1" /> Nuevo reto
      </Button>
      <Label className="block text-[11px] text-gray-500">Los cambios se guardan al pulsar "Guardar" en el evento.</Label>
    </div>
  );
}
